/**
 * Saved Views — small, opinionated store for naming filter combos.
 *
 * A "view" is a named snapshot of the current dashboard URL query
 * (range, store, compare). Activating a view rewrites the URL — the
 * existing FilterUrlSync bridge then hydrates the filters store. This
 * means saved views work across pages without each page knowing about
 * them.
 *
 * Persistence: localStorage (`storage.getSavedViews/setSavedViews`).
 * No server round-trip required — keeps Stage 1 backend-free.
 */
import { create } from 'zustand';
import { storage, subscribeStorage, type SavedView } from '@/lib/storage';
import { useSettingsStore } from '@/api/bi/settings';
import { filtersToQuery, useFilters } from './filters';

interface SavedViewsState {
  views: SavedView[];
  /** Save the current filter snapshot as a new view; returns the created view. */
  saveCurrent: (name: string, opts?: { icon?: string; pinned?: boolean }) => SavedView;
  /** Update an existing view's metadata or query. */
  update: (id: string, patch: Partial<Omit<SavedView, 'id' | 'updated_at'>>) => void;
  /** Replace an existing view's query with the current filter state. */
  refresh: (id: string) => void;
  /** Remove a view by id. */
  remove: (id: string) => void;
  /** Re-order: pinned first, then by updated_at desc. */
  sorted: () => SavedView[];
  /** Hydrate from localStorage — useful after a clearAll(). */
  reload: () => void;
}

function newId(): string {
  return `sv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function persist(views: SavedView[]) {
  storage.setSavedViews(views);
  // Mirror to the server-backed settings blob (debounced inside the store).
  useSettingsStore.getState().patchLocal({ saved_views: views });
}

function snapshotQuery(): string {
  const s = useFilters.getState();
  return filtersToQuery({
    range: s.range,
    storeIds: s.storeIds,
    compareMode: s.compareMode,
    timeRangeMode: s.timeRangeMode,
  });
}

export const useSavedViews = create<SavedViewsState>((set, get) => ({
  views: storage.getSavedViews(),

  saveCurrent: (name, opts) => {
    const view: SavedView = {
      id: newId(),
      name: name.trim() || 'Untitled view',
      query: snapshotQuery(),
      updated_at: new Date().toISOString(),
      icon: opts?.icon,
      pinned: opts?.pinned ?? false,
    };
    const next = [view, ...get().views].slice(0, 30);
    persist(next);
    set({ views: next });
    return view;
  },

  update: (id, patch) => {
    const next = get().views.map((v) =>
      v.id === id ? { ...v, ...patch, updated_at: new Date().toISOString() } : v,
    );
    persist(next);
    set({ views: next });
  },

  refresh: (id) => {
    const next = get().views.map((v) =>
      v.id === id ? { ...v, query: snapshotQuery(), updated_at: new Date().toISOString() } : v,
    );
    persist(next);
    set({ views: next });
  },

  remove: (id) => {
    const next = get().views.filter((v) => v.id !== id);
    persist(next);
    set({ views: next });
  },

  sorted: () => {
    const list = [...get().views];
    list.sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      return b.updated_at.localeCompare(a.updated_at);
    });
    return list;
  },

  reload: () => set({ views: storage.getSavedViews() }),
}));

// Re-hydrate when the namespaced cache flips (login/logout) or the server
// pushes a fresh blob via `storage.applyServerBlob`.
subscribeStorage(() => {
  useSavedViews.setState({ views: storage.getSavedViews() });
});

/**
 * Apply a saved view: write its query to the URL via the router. Caller
 * passes the navigator (since we don't want this module to import react-router
 * directly — keeps it usable from non-React callers, e.g. tests).
 */
export function applyViewToUrl(
  view: SavedView,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  pathname: string,
): void {
  const search = view.query ? `?${view.query}` : '';
  navigate(`${pathname}${search}`, { replace: true });
}
